import { useRescue } from "@/hooks/useRescue";
import { useRouter } from "next/router";
import { ParsedUrlQuery, ParsedUrlQueryInput } from "querystring";
import { createContext, MutableRefObject, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

const CASHBACK_RATES_FIELDS = [
  'rate_pix',
  'rate_cartao',
  'rate_pospago',
  'vehicle_rate'
];

export interface CheckoutQueryParams extends ParsedUrlQuery {
  coupon?: string;
  _coupon?: string;
  cashback?: string;
  rate_pix?: string;
  rate_cartao?: string;
  rate_pospago?: string;
  vehicle_rate?: string;
}

interface CashbackContextInterface {
  isCashback?: boolean;
  setIsCashback: (cashback: boolean) => void;
  rescueValue: number;
  enableCashbackMode: (dataValues: CashbackModePayload) => void;
  disableCashbackMode: () => Promise<ParsedUrlQuery | undefined>;
  ignoreNextUpdateRef: MutableRefObject<boolean>;
}

const Context = createContext<CashbackContextInterface>({} as CashbackContextInterface);

type CashbackRateKey = typeof CASHBACK_RATES_FIELDS[number];
type QueryType = string | ParsedUrlQueryInput | null | undefined;

type CashbackModePayload = {
  rescueValue: string | number;
} & Record<CashbackRateKey, string | number>;

export function CashbackProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const { query, pathname, isReady } = router;

  const { data: rescue } = useRescue();

  const [isCashback, setIsCashback] = useState(false);
  const [backupQuery, setBackupQuery] = useState<QueryType>(undefined);

  const isChangeProcessRef = useRef(false);
  const ignoreNextUpdateRef = useRef(false);

  useEffect(() => {
    if (isReady) {
      if (query.cashback) {
        setIsCashback(true);
      } else {
        setIsCashback(false);
      }
    }
  }, [isReady, query.cashback]);

  useEffect(() => {
    if (query.cashback) {
      disableCashbackMode();
    }
  }, [pathname]);

  useEffect(() => {
    if (!ignoreNextUpdateRef.current && query.cashback) {
      disableCashbackMode();
      ignoreNextUpdateRef.current = false;
    }
  }, [pathname]);

  const updateUrl = async (newQuery: QueryType) => {
    await router.replace(
      { pathname, query: newQuery },
      undefined,
      { shallow: true }
    );
  };

  const enableCashbackMode = async (dataValues: CashbackModePayload) => {
    if (!isChangeProcessRef.current) {
      isChangeProcessRef.current = true;

      setBackupQuery({ ...query });

      const nextQuery = { ...query };

      if (nextQuery.coupon) {
        nextQuery._coupon = nextQuery.coupon;
        delete nextQuery.coupon;
      }

      const newParams: Partial<CheckoutQueryParams> = {
        cashback: String(dataValues.rescueValue),
      };

      CASHBACK_RATES_FIELDS.forEach((rateKey) => {
        if (dataValues[rateKey] !== undefined && dataValues[rateKey] !== null) {
          newParams[rateKey] = String(dataValues[rateKey]);
        }
      });

      const finalQuery = { ...nextQuery, ...newParams };

      await updateUrl(finalQuery);
      setIsCashback(true);
      isChangeProcessRef.current = false;
    }
  }

  const disableCashbackMode = async () => {
    if (!isChangeProcessRef.current) {
      isChangeProcessRef.current = true;

      let queryToRestore: QueryType;

      if (backupQuery) {
        queryToRestore = backupQuery;
        setBackupQuery(null);
      } else {
        const queryCleaned = { ...query };

        delete queryCleaned.cashback;
        CASHBACK_RATES_FIELDS.forEach(field => delete queryCleaned[field]);

        if (queryCleaned._coupon) {
          queryCleaned.coupon = queryCleaned._coupon;
          delete queryCleaned._coupon;
        }

        queryToRestore = queryCleaned;
      }

      await updateUrl(queryToRestore);

      setIsCashback(false);
      isChangeProcessRef.current = false;
      return queryToRestore as ParsedUrlQuery;
    }

    return undefined;
  }

  return (
    <Context.Provider value={{ isCashback, setIsCashback, rescueValue: rescue?.rescueValue || 0, enableCashbackMode, disableCashbackMode, ignoreNextUpdateRef }}>
      {children}
    </Context.Provider>
  )
}

export function useCashback() {
  return useContext(Context);
}